import { differenceInCalendarDays } from 'date-fns'
import { toZonedTime } from 'date-fns-tz'

/**
 * Estado de vencimiento de una tarea.
 *
 * `none` = sin fecha · `done` = completada (gana sobre cualquier fecha) ·
 * `overdue` = vencio · `today` = vence hoy · `soon` = dentro del horizonte ·
 * `upcoming` = mas alla del horizonte.
 */
export type DueStatus = 'none' | 'done' | 'overdue' | 'today' | 'soon' | 'upcoming'

export interface DueStatusOptions {
  /** Momento contra el que se compara. Default: ahora. */
  now?: Date
  /** Timezone IANA del usuario. Default: la del navegador. */
  timeZone?: string
  /** Cuantos dias hacia adelante cuentan como `soon`. Default: 3 (T-3 del motor de notificaciones). */
  soonThresholdDays?: number
  /** Si la tarea ya esta completada. Gana sobre la fecha. */
  isDone?: boolean
}

export type DueDateInput = Date | string | number | null | undefined

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/

export function resolveTimeZone(timeZone?: string): string {
  if (timeZone) return timeZone
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

/**
 * Lleva una fecha al calendario de `timeZone`.
 *
 * Una fecha sin hora (`2026-09-17`) es un DIA, no un instante: si la pasaramos
 * por `new Date()` quedaria a medianoche UTC y en Buenos Aires seria el dia
 * anterior a las 21:00. Por eso se construye directo con sus componentes.
 *
 * **El `setFullYear` no es decorativo.** `new Date(año, mes, día)` mapea los
 * años **0 a 99 a 1900+año**: `new Date(1, 0, 1)` no es el 1 de enero del año
 * 1, es 1901. El QA funcional lo encontró cargando `0001-01-01`, que se mostraba
 * con un desvío de mil novecientos años (6.3). Nadie va a cargar el año 1 —el
 * valor del hallazgo no es ese— pero decía que la aritmética de fechas, que es
 * la lógica más visible del producto, no tenía guardas. `setFullYear` escribe el
 * año tal cual, sin la regla de los dos dígitos.
 */
function toCalendarDate(value: Date | string | number, timeZone: string): Date | null {
  if (typeof value === 'string') {
    const dateOnly = DATE_ONLY.exec(value)
    if (dateOnly) {
      const [, year, month, day] = dateOnly
      const fecha = new Date(2000, Number(month) - 1, Number(day), 12, 0, 0, 0)
      fecha.setFullYear(Number(year))
      return fecha
    }
  }
  const instant = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(instant.getTime())) return null
  return toZonedTime(instant, timeZone)
}

/**
 * Cuantos dias CALENDARIO faltan para la fecha, evaluados en la timezone del
 * usuario. No es `(due - now) / 86400000`: a las 23:30 de Buenos Aires una
 * tarea que vence hoy sigue venciendo hoy, aunque en UTC ya sea manana.
 */
export function getDaysUntilDue(date: DueDateInput, options: DueStatusOptions = {}): number | null {
  if (date === null || date === undefined || date === '') return null

  const timeZone = resolveTimeZone(options.timeZone)
  const due = toCalendarDate(date, timeZone)
  if (!due) return null

  const now = toZonedTime(options.now ?? new Date(), timeZone)
  return differenceInCalendarDays(due, now)
}

/**
 * El día de HOY (± `offsetDays`) en la timezone del usuario, como `YYYY-MM-DD`.
 *
 * **Es la única fuente de "hoy" del frontend.** Antes había tres reglas
 * distintas para la misma pregunta: la de `getDaysUntilDue` (día calendario en
 * la timezone resuelta), la del recorte de "Culminado" del tablero (componentes
 * locales del navegador pasados por `Date.UTC`) y la de los atajos del
 * selector de vencimiento (componentes locales a secas). Coincidían por
 * casualidad —las tres caen en la zona del navegador— y dejaban de coincidir en
 * cuanto alguien pasaba una `timeZone` explícita.
 *
 * Ahora las tres preguntan acá, y `timeZone` viaja igual que en
 * `getDueStatus`: mismo default (`resolveTimeZone`) y mismo significado.
 */
export function calendarDay(offsetDays = 0, options: DueStatusOptions = {}): string {
  const timeZone = resolveTimeZone(options.timeZone)
  const now = toZonedTime(options.now ?? new Date(), timeZone)
  const day = new Date(now.getFullYear(), now.getMonth(), now.getDate() + offsetDays)
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${String(day.getFullYear())}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}`
}

/**
 * Cuántos días CALENDARIO pasaron desde la fecha. El espejo de
 * `getDaysUntilDue`, para lo que mira hacia atrás (el recorte de "Culminado").
 *
 * `null` = sin fecha o fecha inválida, que no es lo mismo que cero días.
 */
export function getDaysSince(date: DueDateInput, options: DueStatusOptions = {}): number | null {
  const days = getDaysUntilDue(date, options)
  // `0 - days` y no `-days`: lo segundo devuelve `-0` para hoy, que compara
  // distinto de `0` con `Object.is` y hace fallar un assert por nada.
  return days === null ? null : 0 - days
}

export function getDueStatus(date: DueDateInput, options: DueStatusOptions = {}): DueStatus {
  if (options.isDone) return 'done'

  const days = getDaysUntilDue(date, options)
  if (days === null) return 'none'

  const threshold = options.soonThresholdDays ?? 3

  if (days < 0) return 'overdue'
  if (days === 0) return 'today'
  if (days <= threshold) return 'soon'
  return 'upcoming'
}

/*
 * Acá vivía `DUE_STATUS_LABEL`, el copy de cada estado en español, y después
 * `DUE_STATUS_KEY`, que devolvía la clave de catálogo en vez del texto.
 *
 * No queda ninguno de los dos. Este módulo es lógica de calendario y no tiene
 * idioma, y resultó que nadie llamaba a la tabla: `DueDateBadge` arma su texto
 * con `due.relative.*`, que sabe contar los días, y los tramos de `/due-soon`
 * usan `due.bucket.*`, que habla en plural porque nombra un grupo. Una tabla de
 * traducciones que nadie consulta se sigue traduciendo a tres idiomas cada vez
 * que alguien revisa el catálogo: es trabajo que se paga tres veces por una
 * pantalla que no existe. Lo que sí sobrevive son `DUE_STATUS_ICON` y
 * `DUE_STATUS_VAR`, que son ícono y color — los mismos en todo idioma.
 */

/** Todo estado se comunica con color + icono + texto, nunca solo color. */
export const DUE_STATUS_ICON: Record<DueStatus, string> = {
  none: '–',
  done: '✓',
  overdue: '!',
  today: '●',
  soon: '◐',
  upcoming: '○',
}

export const DUE_STATUS_VAR: Record<DueStatus, string> = {
  none: '--due-none',
  done: '--due-done',
  overdue: '--due-overdue',
  today: '--due-today',
  soon: '--due-soon',
  upcoming: '--due-upcoming',
}

export function dueStatusColor(status: DueStatus, alpha = 1): string {
  const token = DUE_STATUS_VAR[status]
  return alpha === 1 ? `hsl(var(${token}))` : `hsl(var(${token}) / ${alpha})`
}
